import type { App, UpdateApp } from "@biteasy/contracts";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Accordion } from "@/components/Accordion";
import { FieldGroup, Fieldset } from "@/components/catalyst/Fieldset";
import { useZodServerError } from "@/hooks/use-zod-server-error";
import { orpc } from "@/orpc/client";
import { useAutosaveSection } from "../hooks/use-auto-save";
import { AppStatusSection } from "./components/AppStatusSection";
import { ArchiveAppSection } from "./components/ArchiveAppSection";
import { AttestationSection } from "./components/AttestationSection";
import { BillingEntitySection } from "./components/BillingEntitySection";
import { GeneralSettingsSection } from "./components/GeneralSettingsSection";
import { IapProviderSection } from "./components/IapProviderSection";
import { PartnershipsSection } from "./components/PartnershipsSection";
import type { AttestationDraft, IapConfigEntry } from "./types";

/** Placeholder returned by API for encrypted fields */
const ENCRYPTED_PLACEHOLDER = "********";

export function AppSettings({
	onOpenPartnersDialog,
}: {
	onOpenPartnersDialog: () => void;
}) {
	const { appId } = useParams({ from: "/_app/apps/$appId/" });
	const { sections } = useSearch({ from: "/_app/apps/$appId/" });
	const navigate = useNavigate();
	const { setZodServerError } = useZodServerError();

	const { data: app } = useSuspenseQuery(
		orpc.apps.detail.queryOptions({ input: { id: appId } }),
	);

	const { data: billingEntities } = useSuspenseQuery(
		orpc.billingEntities.list.queryOptions({ input: undefined }),
	);

	// ========= Autosave Sections =========
	const platforms = useAutosaveSection<UpdateApp["platforms"]>({
		initialValue: app.platforms,
		buildPayload: (value) => ({
			id: appId,
			platforms: value,
		}),
		onError: setZodServerError,
	});

	const active = useAutosaveSection<boolean>({
		initialValue: app.active,
		buildPayload: (value) => ({
			id: appId,
			active: value,
		}),
		onError: setZodServerError,
	});

	const billingEntity = useAutosaveSection<string | null>({
		initialValue: app.billingEntityId,
		buildPayload: (value) => ({
			id: appId,
			...(value ? { billingEntityId: value } : {}),
		}),
		onError: setZodServerError,
	});

	const attributionMode = useAutosaveSection<"code" | "window">({
		initialValue: app.attributionMode,
		buildPayload: (value) => ({
			id: appId,
			attributionMode: value,
		}),
		onError: setZodServerError,
	});

	const corsOrigins = useAutosaveSection<string>({
		initialValue: app.allowedCorsOrigins ?? "",
		buildPayload: (value) => ({
			id: appId,
			allowedCorsOrigins: value,
		}),
		onError: setZodServerError,
	});

	const iosStoreLink = useAutosaveSection<string>({
		initialValue: app.iosStoreLink ?? "",
		buildPayload: (value) => ({
			id: appId,
			iosStoreLink: value || null,
		}),
		onError: setZodServerError,
	});

	const androidStoreLink = useAutosaveSection<string>({
		initialValue: app.androidStoreLink ?? "",
		buildPayload: (value) => ({
			id: appId,
			androidStoreLink: value || null,
		}),
		onError: setZodServerError,
	});

	const attestation = useAutosaveSection<AttestationDraft>({
		initialValue:
			app.attestationProvider === "firebase"
				? {
						provider: "firebase",
						projectNumber:
							typeof app.attestationConfig === "object" &&
							app.attestationConfig !== null &&
							"projectNumber" in app.attestationConfig
								? String(app.attestationConfig.projectNumber)
								: "",
					}
				: { provider: "none" },

		buildPayload: (draft) => {
			if (draft.provider === "none") {
				return {
					id: appId,
					attestationProvider: "none",
					attestationConfig: undefined,
				};
			}

			return {
				id: appId,
				attestationProvider: "firebase",
				attestationConfig: {
					projectNumber: draft.projectNumber,
				},
			};
		},

		onError: setZodServerError,
	});

	// ========= Multi-IAP Configs =========
	const iapConfigs = useAutosaveSection<IapConfigEntry[]>({
		initialValue: getInitialIapConfigs(app.iapConfigs as unknown[] | null),

		buildPayload: (configs) => ({
			id: appId,
			iapConfigs: configs.length > 0 ? configs : null,
		}),

		onError: setZodServerError,
	});

	/**
	 * Check if a sensitive field has an existing encrypted value for a specific provider.
	 */
	const hasExistingSecret = (provider: string, field: string): boolean => {
		const configs = app.iapConfigs as Record<string, unknown>[] | null;
		if (!Array.isArray(configs)) return false;
		const entry = configs.find(
			(c: Record<string, unknown>) => c.provider === provider,
		);
		if (!entry) return false;
		return entry[field] === ENCRYPTED_PLACEHOLDER;
	};

	return (
		<Fieldset>
			<FieldGroup className="grid grid-cols-1 gap-x-12 gap-y-10 sm:grid-cols-2">
				{/* App Status & Platforms */}
				<AppStatusSection active={active} platforms={platforms} />

				{/* Billing Entity (Payment Method) Selector */}
				<BillingEntitySection
					appId={app.id}
					billingEntities={billingEntities}
					billingEntity={billingEntity}
				/>

				{/* Advanced Settings Accordion */}
				<div className="col-span-full">
					<Accordion
						multiple
						defaultValue={["general", "attestation", "iap"]}
						value={sections.length > 0 ? sections : undefined}
						onValueChange={(value) =>
							navigate({
								to: "/apps/$appId",
								params: { appId },
								search: (prev) => ({
									...prev,
									tab: prev.tab as "referral_links" | "settings",
									sections: value,
								}),
							})
						}
					>
						<GeneralSettingsSection
							attributionMode={attributionMode}
							corsOrigins={corsOrigins}
							iosStoreLink={iosStoreLink}
							androidStoreLink={androidStoreLink}
							platforms={platforms.value}
						/>
						<AttestationSection attestation={attestation} />
						<IapProviderSection
							iapConfigs={iapConfigs}
							hasExistingSecret={hasExistingSecret}
						/>
						<ArchiveAppSection
							appId={app.id}
							appName={app.name}
							isArchivable={app.computed.isArchivable}
						/>
					</Accordion>
				</div>

				{/* Partnerships */}
				<PartnershipsSection onOpenPartnersDialog={onOpenPartnersDialog} />
			</FieldGroup>
		</Fieldset>
	);
}

/**
 * Extract IAP configs from the app's iapConfigs array.
 * Clears encrypted placeholders to empty strings for editing.
 */
function getInitialIapConfigs(rawConfigs: unknown[] | null): IapConfigEntry[] {
	if (!Array.isArray(rawConfigs)) return [];

	const getSecretValue = (val: unknown): string => {
		const str = String(val ?? "");
		return str === ENCRYPTED_PLACEHOLDER ? "" : str;
	};

	return rawConfigs
		.filter(
			(c): c is Record<string, unknown> =>
				typeof c === "object" && c !== null && "provider" in c,
		)
		.map((config): IapConfigEntry | null => {
			switch (config.provider) {
				case "revenuecat":
					return {
						provider: "revenuecat",
						webhookAuthorizationSecret: getSecretValue(
							config.webhookAuthorizationSecret,
						),
					};
				case "apple":
					return {
						provider: "apple",
						bundleId: String(config.bundleId ?? ""),
						appAppleId: String(config.appAppleId ?? ""),
						issuerId: String(config.issuerId ?? ""),
						keyId: String(config.keyId ?? ""),
						encodedKey: getSecretValue(config.encodedKey),
					};
				case "google":
					return {
						provider: "google",
						packageName: String(config.packageName ?? ""),
						pubsubTopic: String(config.pubsubTopic ?? ""),
						serviceAccountJson: getSecretValue(config.serviceAccountJson),
					};
				default:
					return null;
			}
		})
		.filter((c): c is IapConfigEntry => c !== null);
}

export function defaultAttestationConfig(provider: App["attestationProvider"]) {
	switch (provider) {
		case "none":
			return "";
		case "firebase":
			return JSON.stringify({ projectNumber: "" }, null, 2);
	}
}
